import { executeQuery } from "src/store";
import { baseService } from "src/store/base.store";
import { slotRequestRepository } from "./slot-request.repository";
import { SlotRequest } from "./types/slot-request.entity";

export type SlotRequestsFilter = Partial<Pick<SlotRequest, "coachId" | "learnerId">> & {
  requestId?: string;
};

const matchesFilters = (request: SlotRequest, filters: Partial<SlotRequestsFilter>) => {
  const { requestId: _requestId, ...restFilters } = filters;
  return Object.entries(restFilters).every(([key, value]) => value == null || (request as any)[key] === value);
};

const slotRequestsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getSlotRequests: builder.query<
      (SlotRequest & { id: string })[],
      { where?: SlotRequestsFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where = {}, offset, limit }) => {
        if (where.requestId) {
          const result = await executeQuery(() => slotRequestRepository.findById(where.requestId!));
          if ("error" in result) return { error: result.error } as any;

          const request = result.data as (SlotRequest & { id: string }) | null;
          return { data: request && matchesFilters(request, where) ? [request] : [] } as any;
        }

        const result = await executeQuery(() =>
          slotRequestRepository.find({ where, offset, limit, orderBy: ["requestedDate", "desc"] })
        );
        if ("error" in result) return { error: result.error } as any;
        return { data: result.data as (SlotRequest & { id: string })[] };
      },
    }),

    countSlotRequests: builder.query<number, { where?: SlotRequestsFilter }>({
      queryFn: async ({ where = {} }) => {
        if (where.requestId) {
          const result = await executeQuery(() => slotRequestRepository.findById(where.requestId!));
          if ("error" in result) return { error: result.error } as any;

          const request = result.data as SlotRequest | null;
          return { data: request && matchesFilters(request, where) ? 1 : 0 } as any;
        }

        return executeQuery(() => slotRequestRepository.count(where)) as any;
      },
    }),

    getSlotRequestById: builder.query<SlotRequest & { id: string }, string>({
      queryFn: async (id) => {
        const result = await executeQuery(() => slotRequestRepository.findById(id));
        if ("error" in result) return { error: result.error } as any;
        return { data: result.data as SlotRequest & { id: string } };
      },
    }),
  }),
});

export const { useGetSlotRequestsQuery, useCountSlotRequestsQuery, useGetSlotRequestByIdQuery } = slotRequestsService;
